/**
 * Exit-intent newsletter popup — desktop only.
 *
 * Opens once per visitor via a genuine exit-intent signal (mouse leaving the
 * viewport through the top edge), or a dwell-time fallback for desktop
 * visitors who never trigger that signal during their visit. Touch devices
 * have no exit-intent signal to key off, and the popup shouldn't interrupt a
 * mobile visit at all, so both triggers are gated to fine-pointer devices.
 * Dismissing (close, Escape, or submitting the form) is remembered in
 * localStorage so it never shows again for that visitor.
 */
const DISMISSED_KEY = 'kadnerExitPopupDismissed';
const DESKTOP_FALLBACK_DELAY_MS = 45000;

export function initExitIntentPopup() {
	const popup = document.querySelector('.js-exit-popup');
	if (!popup) {
		return;
	}

	const isDismissed = () => {
		try {
			return localStorage.getItem(DISMISSED_KEY) === '1';
		} catch (error) {
			return false;
		}
	};

	const remember = () => {
		try {
			localStorage.setItem(DISMISSED_KEY, '1');
		} catch (error) {
			// Storage unavailable (e.g. private browsing) — nothing to persist.
		}
	};

	if (isDismissed()) {
		return;
	}

	let previousActiveElement = null;

	// Uniformly shrink the dialog (laid out at the exact Figma 1159×780) to fit
	// the viewport. CSS can't derive a unitless ratio from viewport units, so the
	// scale factor is computed here and applied via `--exit-scale`. The ≤900px
	// stacked layout overrides `--exit-scale` to 1 in CSS, so this is a no-op there.
	const DESIGN_W = 1159;
	const DESIGN_H = 780;
	const VIEWPORT_MARGIN = 40;
	const dialog = popup.querySelector('.exit-popup__dialog');

	const syncScale = () => {
		if (!dialog) {
			return;
		}
		// Measure rather than trust the constants: the dialog grows past the
		// 780px design height when the CMS copy wraps further than the Figma
		// placeholder. offsetWidth/Height are layout sizes, so the transform
		// already applied does not feed back into them.
		const width = dialog.offsetWidth || DESIGN_W;
		const height = dialog.offsetHeight || DESIGN_H;
		const scale = Math.min(
			1,
			(window.innerWidth - VIEWPORT_MARGIN) / width,
			(window.innerHeight - VIEWPORT_MARGIN) / height,
		);
		dialog.style.setProperty('--exit-scale', String(scale));
	};

	const open = () => {
		if (!popup.hidden || isDismissed()) {
			return;
		}
		previousActiveElement = document.activeElement;
		popup.hidden = false;
		popup.setAttribute('aria-hidden', 'false');
		document.body.classList.add('is-exit-popup-open');
		// Measure after unhiding, not before: offsetWidth/Height are 0 on a
		// display:none ancestor, which silently fell back to the Figma design
		// constants below and ignored the dialog's actual rendered size.
		syncScale();
		popup.querySelector('.js-exit-popup-close')?.focus({ preventScroll: true });
	};

	window.addEventListener('resize', syncScale, { passive: true });

	const close = () => {
		popup.hidden = true;
		popup.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('is-exit-popup-open');
		if (previousActiveElement instanceof HTMLElement) {
			previousActiveElement.focus();
		}
		previousActiveElement = null;
		remember();
	};

	document.addEventListener('click', (event) => {
		if (event.target.closest('.js-exit-popup-close')) {
			close();
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && !popup.hidden) {
			close();
		}
	});

	// Submit the signup in place (#154): the visitor stays in the popup and
	// the form is swapped for a thank-you state with an explicit "PDF
	// herunterladen" button, instead of being redirected to another page. The
	// contact-form plugin answers JSON when asked for it, and ignores the
	// form's redirect input on such requests. If the request itself fails
	// (network, non-JSON answer) fall back to a regular submit, which still
	// lands on the thank-you page.
	const form = popup.querySelector('.js-exit-popup-form');
	const success = popup.querySelector('.js-exit-popup-success');
	const errorEl = popup.querySelector('.js-exit-popup-error');

	const showSuccess = () => {
		remember();
		if (form) {
			form.hidden = true;
		}
		// Hide the pitch copy too — the success block carries its own.
		popup
			.querySelectorAll('.exit-popup__content > :not(.js-exit-popup-success):not(.js-exit-popup-form)')
			.forEach((el) => {
				el.hidden = true;
			});
		if (errorEl) {
			errorEl.hidden = true;
		}
		if (success) {
			success.hidden = false;
			(success.querySelector('.js-exit-popup-download') || success.querySelector('a'))?.focus({
				preventScroll: true,
			});
		}
		syncScale();
	};

	form?.addEventListener('submit', async (event) => {
		if (!window.fetch || !success) {
			remember();
			return; // plain submit + redirect
		}
		event.preventDefault();

		const submit = form.querySelector('[type="submit"]');
		if (submit) {
			submit.disabled = true;
		}
		if (errorEl) {
			errorEl.hidden = true;
		}

		try {
			const response = await fetch(form.getAttribute('action') || window.location.href, {
				method: 'POST',
				body: new FormData(form),
				headers: {
					Accept: 'application/json',
					'X-Requested-With': 'XMLHttpRequest',
				},
				credentials: 'same-origin',
			});
			const data = await response.json();
			if (response.ok && data && data.success !== false && !data.errors) {
				showSuccess();
				return;
			}
			if (errorEl) {
				errorEl.hidden = false;
			}
		} catch (error) {
			// Could not complete in place — let the browser do a normal submit.
			remember();
			form.submit();
			return;
		} finally {
			if (submit) {
				submit.disabled = false;
			}
		}
	});

	// Mobile/tablet (coarse pointer): never show the popup — no exit-intent
	// signal exists there, and a dwell-time popup would just interrupt the visit.
	const hasFinePointer = window.matchMedia?.('(pointer: fine)').matches ?? true;
	if (hasFinePointer) {
		document.addEventListener('mouseout', (event) => {
			if (!event.relatedTarget && event.clientY <= 0) {
				open();
			}
		});

		window.setTimeout(open, DESKTOP_FALLBACK_DELAY_MS);
	}
}
